import { useMemo, useRef, useState } from "react";
import { Camera, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MethodChart } from "@/components/MethodChart";
import { FormDialog } from "@/components/ResourceTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { ResourceTable } from "@/components/ResourceTable";
import { useWsData } from "@/hooks/useWsData";
import { api, brl, dateBR, download, errMsg, today } from "@/lib/api";

const STATUS = { pago: "bg-emerald-500/15 text-emerald-300", pendente: "bg-amber-500/15 text-amber-300", agendado: "bg-sky-500/15 text-sky-300", vencido: "bg-red-500/15 text-red-300" };
export const METHODS = [["pix", "PIX"], ["debito", "Cartão de débito"], ["credito", "Cartão de crédito"], ["transferencia", "Transferência (TED/DOC)"],
  ["dinheiro", "Dinheiro"], ["boleto", "Boleto"], ["outros", "Outros"]];
const METHOD_LABEL = Object.fromEntries(METHODS);
const methodOf = (r) => r.payment_method || (r.card_id ? "credito" : "");

export default function Lancamentos() {
  const { data: rows, reload, wid } = useWsData("/transactions");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("todos");
  const [method, setMethod] = useState("todas");

  const fields = useMemo(() => [
    { name: "date", label: "Data", type: "date", default: today() },
    { name: "kind", label: "Tipo", type: "select", default: "despesa", options: [{ value: "despesa", label: "Despesa" }, { value: "receita", label: "Receita" }] },
    { name: "description", label: "Descrição", wide: true },
    { name: "category", label: "Categoria", default: "Geral" },
    { name: "amount", label: "Valor total (R$)", type: "number" },
    { name: "status", label: "Status", type: "select", default: "pago", options: [{ value: "pago", label: "Pago" }, { value: "pendente", label: "Pendente" }, { value: "agendado", label: "Agendado" }, { value: "vencido", label: "Vencido" }] },
    { name: "payment_method", label: "Forma de pagamento", type: "select", default: "pix", options: METHODS.map(([value, label]) => ({ value, label })) },
    { name: "installments", label: "Parcelas (1 = à vista)", type: "number", default: 1, createOnly: true },
  ], []);

  const [month, setMonth] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/workspaces/${wid}/receipt/scan`, fd);
      setReceipt({ ...Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])), ...data, status: "pago" });
      toast.success("Recibo lido! Confira os dados e salve.");
    } catch (e) { toast.error(errMsg(e)); } finally { setScanning(false); }
  };
  const saveReceipt = async (p) => { await api.post(`/workspaces/${wid}/transactions`, p); toast.success("Despesa lançada"); reload(); };
  if (!rows) return <Spinner />;
  const filtered = rows.filter((r) => (kind === "todos" || r.kind === kind) && (!month || r.date.startsWith(month)) && (method === "todas" || methodOf(r) === method) &&
    (!q || `${r.description} ${r.category}`.toLowerCase().includes(q.toLowerCase())));
  const rec = filtered.filter((r) => r.kind === "receita").reduce((a, r) => a + r.amount, 0);
  const des = filtered.filter((r) => r.kind === "despesa").reduce((a, r) => a + r.amount, 0);

  return (
    <div data-testid="lancamentos-page">
      <PageHeader eyebrow="Controle" title="Receitas e despesas" subtitle="Registre receitas e despesas. Para o cartão de crédito, lance o valor total da fatura como uma despesa com forma de pagamento Crédito.">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,.pdf" className="hidden"
          onChange={(e) => { scan(e.target.files?.[0]); e.target.value = ""; }} data-testid="receipt-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="receipt-scan-btn">
          {scanning ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />} {scanning ? "Lendo recibo..." : "Foto do recibo"}
        </Button>
        <Button variant="outline" className="border-[#1E3A6E] bg-transparent" onClick={() => download(`/workspaces/${wid}/export.csv`, "movimentacoes.csv")} data-testid="export-csv-btn">
          <Download className="mr-1 h-4 w-4" /> CSV
        </Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Receitas (filtro)" value={brl(rec)} tone="green" testid="tx-total-income" />
        <Kpi label="Despesas (filtro)" value={brl(des)} tone="red" testid="tx-total-expense" />
        <Kpi label="Saldo (filtro)" value={brl(rec - des)} tone={rec - des >= 0 ? "green" : "red"} testid="tx-balance" />
      </div>
      <MethodChart rows={rows} />
      <Section>
        <div className="mb-4 flex flex-wrap gap-2">
          <Input placeholder="Buscar descrição ou categoria" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-[#0B1A33]" data-testid="tx-search" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm" data-testid="tx-filter-kind">
            <option value="todos">Todos</option><option value="receita">Receitas</option><option value="despesa">Despesas</option>
          </select>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-[#0B1A33]" data-testid="tx-filter-month" />
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="h-10 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm" data-testid="tx-filter-method">
            <option value="todas">Todas as formas</option>{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <ResourceTable wid={wid} res="transactions" title={`${filtered.length} movimentações`} addLabel="Novo lançamento" rows={filtered} onChanged={reload}
          fields={fields} mapEdit={(r) => ({ ...r, payment_method: methodOf(r) || "pix" })}
          columns={[
            { key: "date", label: "Data", render: (r) => dateBR(r.date) },
            { key: "description", label: "Descrição" },
            { key: "category", label: "Categoria" },
            { key: "payment_method", label: "Forma", render: (r) => <span className="text-slate-300" data-testid="tx-method">{METHOD_LABEL[methodOf(r)] || "—"}</span> },
            { key: "status", label: "Status", render: (r) => <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[r.status]}`}>{r.status}</span> },
            { key: "amount", label: "Valor", right: true, render: (r) => <span className={r.kind === "receita" ? "text-emerald-400" : "text-red-400"}>{r.kind === "receita" ? "+" : "−"}{brl(r.amount)}</span> },
          ]} />
      </Section>
      <FormDialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)} title="Confirmar despesa do recibo" fields={fields} initial={receipt} onSubmit={saveReceipt} testid="receipt-dialog" />
    </div>
  );
}
